/**
 * ConfigContext.js
 *
 * Provides live site config to every component via the useConfig() hook.
 *
 * On mount it:
 *   1. Races fetchContentFromServer() (HTTP) against the WebSocket connection_response
 *   2. Whichever resolves first marks the provider as "ready" and sets the real config
 *   3. Children are not rendered until ready — prevents a flash of hardcoded defaults
 *   4. Subsequent WebSocket pushes (admin saves) update config in-place with no reload
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import defaultConfig, {
  applyAdminConfig,
  fetchContentFromServer,
  startConfigSync,
} from './config';

const ConfigContext = createContext(defaultConfig);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(defaultConfig);
  const [ready, setReady]   = useState(false);
  const resolvedRef         = useRef(false); // ensures the gate only opens once

  function resolve(serverOverrides) {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    if (serverOverrides && Object.keys(serverOverrides).length > 0) {
      setConfig(applyAdminConfig(defaultConfig, serverOverrides));
    }
    setReady(true);
  }

  useEffect(() => {
    // Race HTTP fetch vs WebSocket connection_response — first one wins the
    // initial render gate. Both paths still apply their result; the gate just
    // ensures we never paint default content while the real config is in-flight.
    fetchContentFromServer().then(resolve);

    const disconnect = startConfigSync(serverOverrides => {
      if (!resolvedRef.current) {
        // WebSocket won the race — use it to open the gate too
        resolve(serverOverrides);
      } else {
        // Normal live-update path — admin saved a change
        setConfig(applyAdminConfig(defaultConfig, serverOverrides));
      }
    });

    // Safety net: if both HTTP and WebSocket fail or stall, show the page
    // with defaults after 3s rather than hanging indefinitely
    const timeout = setTimeout(() => {
      if (!resolvedRef.current) {
        resolvedRef.current = true;
        setReady(true);
      }
    }, 3000);

    return () => {
      disconnect();
      clearTimeout(timeout);
    };
  }, []);

  if (!ready) {
    // Blank screen matching the site background — no flash, no spinner
    return (
      <div style={{
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        height:          '100vh',
        backgroundColor: 'var(--bg-light, #0a0a0a)',
      }} />
    );
  }

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  return useContext(ConfigContext);
}